const NXT_BODY_CLASS = 'nxtweb';
const OVERLAY_ID = 'nxt-page-loader-overlay';
const STYLE_ID = 'nxt-page-loader-style';
const LOADING_HTML_CLASS = 'nxt-page-loading';
const MAX_WAIT_MS = 5000;

function injectStyles(): void {
    if (document.getElementById(STYLE_ID)) {
        return;
    }
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
        html.${LOADING_HTML_CLASS}, html.${LOADING_HTML_CLASS} body {
            overflow: hidden;
        }
        #${OVERLAY_ID} {
            position: fixed;
            inset: 0;
            z-index: 2147483647;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--background-background-01, #ffffff);
            opacity: 1;
            transition: opacity 0.2s ease-out;
        }
        #${OVERLAY_ID}[data-hidden="true"] {
            opacity: 0;
            pointer-events: none;
        }
        #${OVERLAY_ID} .nxt-page-loader-spinner {
            display: inline-flex;
            width: 40px;
            height: 40px;
        }
        #${OVERLAY_ID} .nxt-page-loader-spinner > svg {
            width: 100%;
            height: 100%;
            animation: nxt-page-loader-spin 0.8s linear infinite;
        }
        #${OVERLAY_ID} .nxt-page-loader-spinner > svg .track {
            stroke: var(--border-border-subtle-01, #e0e0e0);
        }
        #${OVERLAY_ID} .nxt-page-loader-spinner > svg .arc {
            stroke: var(--border-border-focus, #ff6600);
        }
        @keyframes nxt-page-loader-spin {
            to {
                transform: rotate(360deg);
            }
        }
    `;
    document.head.appendChild(style);
}

function createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-label', 'Loading');
    overlay.innerHTML = `
        <span class="nxt-page-loader-spinner">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle class="track" cx="20" cy="20" r="18.5" stroke-width="3"></circle>
                <path class="arc" stroke-width="3" d="M7.70712 33.8252C4.90064 31.3297 2.92008 28.0393 2.02869 24.3911C1.1373 20.743 1.3773 16.9099 2.71679 13.4015C4.05628 9.89301 6.43182 6.87529 9.52766 4.74943C12.6235 2.62356 16.293 1.49023 20.0485 1.50006"></path>
            </svg>
        </span>
    `;
    return overlay;
}

function collectCustomElementTagNames(): string[] {
    const tagNames = new Set<string>();
    document.querySelectorAll('*').forEach((el) => {
        const tagName = el.tagName.toLowerCase();
        if (tagName.includes('-')) {
            tagNames.add(tagName);
        }
    });
    return [...tagNames];
}

function whenAllCustomElementsDefined(): Promise<void> {
    const tagNames = collectCustomElementTagNames();
    if (tagNames.length === 0) {
        return Promise.resolve();
    }
    return Promise.all(tagNames.map((tagName) => customElements.whenDefined(tagName))).then(() => undefined);
}

function withTimeout(promise: Promise<void>, timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, timeoutMs);
        promise.then(() => {
            clearTimeout(timer);
            resolve();
        });
    });
}

function hideOverlay(overlay: HTMLElement): void {
    overlay.setAttribute('data-hidden', 'true');
    document.documentElement.classList.remove(LOADING_HTML_CLASS);
    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    // Safety net in case transitionend never fires (e.g. reduced motion / already removed).
    setTimeout(() => overlay.remove(), 500);
}

function whenDomContentLoaded(): Promise<void> {
    if (document.readyState !== 'loading') {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    });
}

function whenBodyReady(): Promise<HTMLElement> {
    if (document.body) {
        return Promise.resolve(document.body);
    }
    return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
            if (document.body) {
                observer.disconnect();
                resolve(document.body);
            }
        });
        observer.observe(document.documentElement, { childList: true });
    });
}

async function initPageLoader(): Promise<void> {
    const body = await whenBodyReady();
    if (!body.classList.contains(NXT_BODY_CLASS)) {
        return;
    }

    injectStyles();
    document.documentElement.classList.add(LOADING_HTML_CLASS);
    const overlay = createOverlay();
    body.appendChild(overlay);

    await whenDomContentLoaded();
    await withTimeout(whenAllCustomElementsDefined(), MAX_WAIT_MS);

    hideOverlay(overlay);
}

initPageLoader();
